import { TZDate } from '@date-fns/tz';
import { isValidDateRange } from '@shared/utils/dateRange';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isEqual,
  startOfDay,
  startOfMinute,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subHours,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { dateRangeTexts } from '../texts';
import type { DateRange, Preset, PresetCreateContext } from '../types';

const HOUR = 60 * 60;
const DAY = 24 * HOUR;

export type BuiltInPresetId =
  | 'last2hours'
  | 'last6hours'
  | 'last12hours'
  | 'last24hours'
  | 'last7days'
  | 'last30days'
  | 'last90days'
  | 'today'
  | 'yesterday'
  | 'week'
  | 'prevWeek'
  | 'month'
  | 'prevMonth'
  | 'year'
  | 'prevYear';

type BuiltInPresets = {
  [Id in BuiltInPresetId]: Preset & { id: Id };
};

function getNow(context?: PresetCreateContext): Date {
  const now = context?.referenceDate ?? new Date();
  return context?.timeZone ? new TZDate(now, context.timeZone) : now;
}

export const builtInPresets: BuiltInPresets = {
  last2hours: {
    id: 'last2hours',
    label: dateRangeTexts.presets.lastXHours.label,
    values: { numberOfHours: 2 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: subHours(now, 2),
        to: now,
      };
    },
    maxDurationSeconds: 2 * HOUR,
  },
  last6hours: {
    id: 'last6hours',
    label: dateRangeTexts.presets.lastXHours.label,
    values: { numberOfHours: 6 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: subHours(now, 6),
        to: now,
      };
    },
    maxDurationSeconds: 6 * HOUR,
  },
  last12hours: {
    id: 'last12hours',
    label: dateRangeTexts.presets.lastXHours.label,
    values: { numberOfHours: 12 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: subHours(now, 12),
        to: now,
      };
    },
    maxDurationSeconds: 12 * HOUR,
  },
  last24hours: {
    id: 'last24hours',
    label: dateRangeTexts.presets.lastXHours.label,
    values: { numberOfHours: 24 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: subHours(now, 24),
        to: now,
      };
    },
    maxDurationSeconds: DAY,
  },
  last7days: {
    id: 'last7days',
    label: dateRangeTexts.presets.lastXDays.label,
    values: { numberOfDays: 7 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: startOfDay(subDays(now, 6)),
        to: endOfDay(now),
      };
    },
    maxDurationSeconds: 7 * DAY,
  },
  last30days: {
    id: 'last30days',
    label: dateRangeTexts.presets.lastXDays.label,
    values: { numberOfDays: 30 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: startOfDay(subDays(now, 29)),
        to: endOfDay(now),
      };
    },
    maxDurationSeconds: 30 * DAY,
  },
  last90days: {
    id: 'last90days',
    label: dateRangeTexts.presets.lastXDays.label,
    values: { numberOfDays: 90 },
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: startOfDay(subDays(now, 89)),
        to: endOfDay(now),
      };
    },
    maxDurationSeconds: 90 * DAY,
  },
  today: {
    id: 'today',
    label: dateRangeTexts.presets.today.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return {
        from: startOfDay(now),
        to: endOfDay(now),
      };
    },
    maxDurationSeconds: DAY,
  },
  yesterday: {
    id: 'yesterday',
    label: dateRangeTexts.presets.yesterday.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const yesterday = subDays(now, 1);
      return {
        from: startOfDay(yesterday),
        to: endOfDay(yesterday),
      };
    },
    maxDurationSeconds: DAY,
  },
  week: {
    id: 'week',
    label: dateRangeTexts.presets.week.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const weekStartsOn = context?.weekStartsOn ?? 1;
      return {
        from: startOfWeek(now, { weekStartsOn }),
        to: endOfWeek(now, { weekStartsOn }),
      };
    },
    maxDurationSeconds: 7 * DAY,
  },
  prevWeek: {
    id: 'prevWeek',
    label: dateRangeTexts.presets.previousWeek.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const date = subWeeks(now, 1);
      const weekStartsOn = context?.weekStartsOn ?? 1;
      return {
        from: startOfWeek(date, { weekStartsOn }),
        to: endOfWeek(date, { weekStartsOn }),
      };
    },
    maxDurationSeconds: 7 * DAY,
  },
  month: {
    id: 'month',
    label: dateRangeTexts.presets.month.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return { from: startOfMonth(now), to: endOfMonth(now) };
    },
    maxDurationSeconds: 31 * DAY,
  },
  prevMonth: {
    id: 'prevMonth',
    label: dateRangeTexts.presets.previousMonth.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const date = subMonths(now, 1);
      return {
        from: startOfMonth(date),
        to: endOfMonth(date),
      };
    },
    maxDurationSeconds: 31 * DAY,
  },
  year: {
    id: 'year',
    label: dateRangeTexts.presets.year.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      return { from: startOfYear(now), to: endOfYear(now) };
    },
    maxDurationSeconds: 365 * DAY,
  },
  prevYear: {
    id: 'prevYear',
    label: dateRangeTexts.presets.previousYear.label,
    createRange: (context?: PresetCreateContext) => {
      const now = getNow(context);
      const date = subYears(now, 1);
      return {
        from: startOfYear(date),
        to: endOfYear(date),
      };
    },
    maxDurationSeconds: 365 * DAY,
  },
};

interface GetActivePresetOptions<T extends Preset> extends PresetCreateContext {
  presets: readonly T[];
}

export function getActivePreset<T extends Preset>(
  dateRange: DateRange,
  options: GetActivePresetOptions<T>
) {
  if (!isValidDateRange(dateRange)) {
    return;
  }

  const range = {
    from: startOfMinute(dateRange.from),
    to: startOfMinute(dateRange.to),
  };

  return options.presets.find((preset) => {
    const { from, to } = preset.createRange(options);

    return (
      isEqual(range.from, startOfMinute(from)) &&
      isEqual(range.to, startOfMinute(to))
    );
  });
}
